import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Pencil, Trash2, Plus, X } from 'lucide-react';

export default function ManageMilestones() {
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    status: 'planned',
    description: '',
    details: ['']
  });

  useEffect(() => {
    fetchMilestones();
  }, []);

  const fetchMilestones = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'milestones'));
      const milestonesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMilestones(milestonesData.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds));
    } catch (error) {
      console.error('Error fetching milestones:', error);
      alert('Failed to fetch milestones');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const milestoneData = {
        ...formData,
        details: formData.details.filter(d => d.trim() !== '')
      };

      if (editingMilestone) {
        const milestoneRef = doc(db, 'milestones', editingMilestone.id);
        await updateDoc(milestoneRef, {
          ...milestoneData,
          updatedAt: serverTimestamp()
        });
        alert('Milestone updated successfully!');
      } else {
        await addDoc(collection(db, 'milestones'), {
          ...milestoneData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        alert('Milestone added successfully!');
      }

      setFormData({
        title: '',
        date: '',
        status: 'planned',
        description: '',
        details: ['']
      });
      setEditingMilestone(null);
      setShowForm(false);
      fetchMilestones();
    } catch (error) {
      console.error('Error saving milestone:', error);
      alert('Failed to save milestone');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone);
    setFormData({
      title: milestone.title,
      date: milestone.date,
      status: milestone.status,
      description: milestone.description,
      details: milestone.details && milestone.details.length > 0 ? milestone.details : ['']
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this milestone?')) return;

    try {
      await deleteDoc(doc(db, 'milestones', id));
      alert('Milestone deleted successfully!');
      fetchMilestones();
    } catch (error) {
      console.error('Error deleting milestone:', error);
      alert('Failed to delete milestone');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMilestone(null);
    setFormData({
      title: '',
      date: '',
      status: 'planned',
      description: '',
      details: ['']
    });
  };

  const addDetailField = () => {
    setFormData({ ...formData, details: [...formData.details, ''] });
  };

  const removeDetailField = (index) => {
    const newDetails = formData.details.filter((_, i) => i !== index);
    setFormData({ ...formData, details: newDetails.length > 0 ? newDetails : [''] });
  };

  const updateDetailField = (index, value) => {
    const newDetails = [...formData.details];
    newDetails[index] = value;
    setFormData({ ...formData, details: newDetails });
  };

  return (
    <div className="py-20 min-h-screen bg-gradient-to-b from-gray-900 to-black">
      <div className="container-width section-padding">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-white">Manage Milestones</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary flex items-center gap-2"
          >
            {showForm ? (
              <>
                <X className="w-5 h-5" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                Add New Milestone
              </>
            )}
          </button>
        </div>

        {showForm && (
          <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg mb-8 border border-gray-700">
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-white mb-2">Milestone Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  placeholder="Enter milestone title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Date *</label>
                  <input
                    type="text"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    placeholder="e.g. August 2025"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  >
                    <option value="planned">Planned</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="upcoming">Upcoming</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Description *</label>
                <textarea
                  required
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  placeholder="Enter milestone description"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Details</label>
                {formData.details.map((detail, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={detail}
                      onChange={(e) => updateDetailField(index, e.target.value)}
                      className="flex-1 px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                      placeholder="Enter detail point"
                    />
                    {formData.details.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDetailField(index)}
                        className="px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addDetailField}
                  className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Detail Point
                </button>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex-1"
                >
                  {loading ? 'Saving...' : editingMilestone ? 'Update Milestone' : 'Add Milestone'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-700">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Title</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Date</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Status</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Description</th>
                  <th className="px-6 py-4 text-right text-white font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {milestones.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                      No milestones yet. Add your first milestone!
                    </td>
                  </tr>
                ) : (
                  milestones.map((milestone) => (
                    <tr key={milestone.id} className="hover:bg-gray-900/30 transition-colors">
                      <td className="px-6 py-4 text-white font-medium">{milestone.title}</td>
                      <td className="px-6 py-4 text-gray-300">{milestone.date}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          milestone.status === 'completed' 
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                            : milestone.status === 'in-progress'
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            : milestone.status === 'upcoming'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                        }`}>
                          {milestone.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300 max-w-md truncate">{milestone.description}</td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(milestone)}
                            className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            title="Edit milestone"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(milestone.id)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Delete milestone"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={() => navigate('/projects')}
            className="text-primary-400 hover:text-primary-300 transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
